'use client'

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/providers/AuthContext'
import {
  fetchNotificationsByStatus,
  fetchNotificationsOverview,
} from '@/services/notifications'
import type {
  NotificationItem,
  NotificationListingStatus,
  NotificationOverviewResponse,
  NotificationStatus,
} from '@/services/notifications/types'

/**
 * Intervalo de poll do `GET /notifications`. O poll é o piso de resync — o back
 * também emite `notifications_changed` no socket (ver `useNotificationsLive`),
 * mas rede/socket caem e o inbox não pode ficar mudo. Ajustável por chamada ou
 * globalmente via `NEXT_PUBLIC_NOTIFICATIONS_POLL_MS`.
 */
export const DEFAULT_NOTIFICATIONS_POLL_MS = (() => {
  const fromEnv = Number(process.env.NEXT_PUBLIC_NOTIFICATIONS_POLL_MS)
  return Number.isFinite(fromEnv) && fromEnv > 0 ? fromEnv : 10_000
})()

export const notificationKeys = {
  all: ['notifications'] as const,
  overview: () => [...notificationKeys.all, 'overview'] as const,
  list: (status: NotificationListingStatus, start?: number, end?: number) =>
    [...notificationKeys.all, 'list', status, start ?? null, end ?? null] as const,
}

interface UseNotificationsOverviewOptions {
  /** Poll em ms. Default: `DEFAULT_NOTIFICATIONS_POLL_MS`. */
  pollInterval?: number
  /** `false` desliga o poll — a query só roda em mount/focus. */
  enablePolling?: boolean
}

/** Fonte única do sino: as `new` inteiras + contadores dos 4 status. */
export function useNotificationsOverview(options: UseNotificationsOverviewOptions = {}) {
  const { user } = useAuth()
  const { pollInterval = DEFAULT_NOTIFICATIONS_POLL_MS, enablePolling = true } = options

  return useQuery<NotificationOverviewResponse>({
    queryKey: notificationKeys.overview(),
    queryFn: fetchNotificationsOverview,
    enabled: !!user,
    refetchInterval: enablePolling ? pollInterval : false,
    // Aba escondida não gasta requisição — o toast/browser notif de quem volta
    // é resolvido pelo refetch de foco.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: Math.min(pollInterval, 30_000),
  })
}

/** Listagem por status. `new` não existe aqui — só via overview. */
export function useNotificationsByStatus(
  status: NotificationListingStatus,
  range?: { start?: number; end?: number },
) {
  const { user } = useAuth()

  return useQuery<NotificationItem[]>({
    queryKey: notificationKeys.list(status, range?.start, range?.end),
    queryFn: () => fetchNotificationsByStatus({ status, ...range }),
    enabled: !!user,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

export type ResolvedNotificationStatus = NotificationStatus | null

export interface UseNotificationByIdResult {
  data: NotificationItem | null
  status: ResolvedNotificationStatus
  isLoading: boolean
}

/**
 * Procura uma notification por id nas 4 listas (overview + 3 status) e deriva
 * de qual delas veio. Aberta direto por URL, dispara as 4 em paralelo; vinda da
 * lista, serve do cache do TanStack na hora.
 */
export function useNotificationById(id: string): UseNotificationByIdResult {
  const overview = useNotificationsOverview()
  const read = useNotificationsByStatus('read')
  const missed = useNotificationsByStatus('missed')
  const deleted = useNotificationsByStatus('deleted')

  const inNew = overview.data?.new.find((n) => n.id === id)
  if (inNew) return { data: inNew, status: 'new', isLoading: false }

  const inRead = read.data?.find((n) => n.id === id)
  if (inRead) return { data: inRead, status: 'read', isLoading: false }

  const inMissed = missed.data?.find((n) => n.id === id)
  if (inMissed) return { data: inMissed, status: 'missed', isLoading: false }

  const inDeleted = deleted.data?.find((n) => n.id === id)
  if (inDeleted) return { data: inDeleted, status: 'deleted', isLoading: false }

  const isLoading =
    overview.isLoading || read.isLoading || missed.isLoading || deleted.isLoading

  return { data: null, status: null, isLoading }
}
